import { Application, extend } from "@pixi/react";
import { Container, Graphics, Sprite, Text } from "pixi.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Room } from "colyseus.js";
import { EngineHost } from "@app/EngineHost";
import { DebugPanel } from "@app/hud/DebugPanel";
import { InspectorPanel } from "@app/hud/InspectorPanel";
import { WorldMinimap } from "@game/hud/WorldMinimap";
import { PlayerStatusBar } from "@app/hud/PlayerStatusBar";
import { ActionBar, type ActionSlot } from "@app/hud/ActionBar";
import type { EngineContextValue } from "@app/engine-context";
import { useGame } from "@game/game-context";
import { GameChat } from "@game/hud/GameChat";
import { NotificationToaster } from "@game/hud/NotificationToaster";
import { WorldClientScene } from "@game/scenes/WorldClientScene";
import type { GameSession } from "@game/game-context";
import type {
  GlobalState,
  Wallet,
  WorldEncounterEvent,
  WorldNpcInteractEvent,
  WorldTeleportEvent,
} from "@game/types";

extend({ Container, Graphics, Sprite, Text });

interface Props {
  session: GameSession;
  /** Active global room (chat + notifications). May be null while connecting. */
  globalRoom: Room<GlobalState> | null;
}

export function WorldScreen({ session, globalRoom }: Props) {
  const game = useGame();
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const [engine, setEngine] = useState<EngineContextValue | null>(null);
  const [zoneEpoch, setZoneEpoch] = useState(0);
  const [healCooldownEndsAt, setHealCooldownEndsAt] = useState<number | undefined>(undefined);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auto-dismiss transient toasts
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Real overworld actions (skills are battle-only; out here they'd be confusing).
  const actionSlots: ActionSlot[] = useMemo(
    () => [
      {
        key: "interact",
        hotkey: "E",
        name: "Interact (NPC / portal / boss within range)",
        iconChar: "✦",
        color: "#facc15",
        // Real interact is captured by WorldClientScene's window-level keydown.
        // The button just provides discoverability; clicking it focuses the
        // canvas so the next E presses also work.
        onActivate: () => engine?.game.app.canvas.focus(),
      },
      {
        key: "heal",
        hotkey: "H",
        name: "Heal party (free; cooldown)",
        iconChar: "✚",
        color: "#4ade80",
        cooldownDurationS: 60,
        ...(healCooldownEndsAt !== undefined ? { cooldownEndsAt: healCooldownEndsAt } : {}),
        onActivate: async () => {
          try {
            await game.client.api.post("/digimon/heal");
            setHealCooldownEndsAt(performance.now() + 60_000);
            setToast("Party healed");
          } catch (err) {
            setToast(`Heal failed: ${(err as Error).message}`);
          }
        },
      },
      {
        key: "mount",
        hotkey: "M",
        name: "Toggle mount",
        iconChar: "🐎",
        color: "#a78bfa",
        onActivate: async () => {
          try {
            const cur = session.position.mountDigimonId;
            const next = cur ? null : firstChampionPlus(session)?.id ?? null;
            if (!cur && !next) {
              setToast("No Champion+ digimon to mount");
              return;
            }
            await game.client.api.post("/world/mount", { digimonId: next });
            session.position.mountDigimonId = next;
            setToast(next ? "Mounted" : "Dismounted");
          } catch (err) {
            setToast(`Mount: ${(err as Error).message}`);
          }
        },
      },
      {
        key: "companion",
        hotkey: "C",
        name: "Toggle companion",
        iconChar: "👥",
        color: "#7dd3fc",
        onActivate: async () => {
          try {
            const cur = session.position.companionDigimonId;
            const next = cur ? null : firstParty(session)?.id ?? null;
            await game.client.api.post("/world/companion", { digimonId: next });
            session.position.companionDigimonId = next;
            setToast(next ? "Companion summoned" : "Companion dismissed");
          } catch (err) {
            setToast(`Companion: ${(err as Error).message}`);
          }
        },
      },
      {
        key: "daily",
        hotkey: "1",
        name: "Claim daily reward",
        iconChar: "📅",
        color: "#fbbf24",
        onActivate: async () => {
          try {
            const r = await game.client.api.post<{
              alreadyClaimed: boolean;
              streak: number;
              bits: number;
              bondTokens: number;
            }>("/daily/claim");
            setToast(
              r.alreadyClaimed
                ? `Already claimed (streak ${r.streak})`
                : `+${r.bits} bits${r.bondTokens ? ` · +${r.bondTokens} bond` : ""} (streak ${r.streak})`,
            );
            const w = await game.client.api.get<Wallet>("/wallet");
            session.wallet = w;
          } catch (err) {
            setToast(`Daily: ${(err as Error).message}`);
          }
        },
      },
      {
        key: "emote",
        hotkey: "2",
        name: "Wave",
        iconChar: "👋",
        color: "#f472b6",
        // We don't have a room handle here — best-effort: focus canvas and let
        // the player use chat for now. (To wire fully, expose room via context.)
        onActivate: () => setToast("Try /emote in chat"),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game, session, engine, healCooldownEndsAt],
  );

  const onEngineReady = useCallback(
    async (ctx: EngineContextValue) => {
      setEngine(ctx);
      const scene = new WorldClientScene({
        client: game.client,
        session,
        callbacks: {
          onEncounter: (e: WorldEncounterEvent) => handleEncounter(e),
          onTeleport: (e: WorldTeleportEvent) => handleTeleport(e),
          onNpcInteract: (e: WorldNpcInteractEvent) => handleNpcInteract(e),
          onError: (msg: string) => setToast(msg),
        },
      });
      await ctx.scenes.go(scene);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game, session],
  );

  // Re-enter WorldClientScene when the zone changes (teleport). We DON'T
  // remount the <Application> — that would lose the GPU context. Instead
  // we just swap the Scene; the canvas / Pixi state stays alive.
  useEffect(() => {
    if (!engine || zoneEpoch === 0) return;
    void (async () => {
      const scene = new WorldClientScene({
        client: game.client,
        session,
        callbacks: {
          onEncounter: (e: WorldEncounterEvent) => handleEncounter(e),
          onTeleport: (e: WorldTeleportEvent) => handleTeleport(e),
          onNpcInteract: (e: WorldNpcInteractEvent) => handleNpcInteract(e),
          onError: (msg: string) => setToast(msg),
        },
      });
      await engine.scenes.go(scene);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneEpoch, engine]);

  const handleEncounter = async (e: WorldEncounterEvent) => {
    try {
      const r = await game.client.api.post<{ roomId: string; encounter: unknown }>(
        "/world/encounter/start",
        { encounterId: e.encounterId },
      );
      game.setStage({ kind: "battle", session, roomId: r.roomId, mode: "pve" });
    } catch (err) {
      setToast(`Encounter failed: ${(err as Error).message}`);
    }
  };

  const handleTeleport = (e: WorldTeleportEvent) => {
    session.position.zone = e.toZone;
    session.position.x = e.x;
    session.position.y = e.y;
    setZoneEpoch((k) => k + 1);
  };

  const handleNpcInteract = (e: WorldNpcInteractEvent) => {
    // Minimal modal — replace with proper dialog UI later.
    const lines = e.dialogue?.[0]?.lines?.join("\n") ?? "(no dialogue)";
    alert(`${e.name} (${e.role})\n\n${lines}`);
  };

  // Pull HP from the active digimon defensively (level/ivHp may be missing
  // before /digimons resolves, or after re-fetch).
  const hp = currentHp(session);
  const hpMax = maxHp(session);

  return (
    <>
      <Application
        width={size.width}
        height={size.height}
        background={0x0b0d12}
        antialias={false}
        autoDensity
        resolution={window.devicePixelRatio}
        roundPixels
        preference="webgl"
      >
        <EngineHost onReady={onEngineReady}>
          <pixiContainer />
        </EngineHost>
      </Application>
      <DebugPanel sceneName={`zone:${session.position.zone}`} game={engine?.game} />
      <InspectorPanel game={engine?.game} />
      <NotificationToaster globalRoom={globalRoom} />
      <GameChat globalRoom={globalRoom} mutedUntil={session.me.mutedUntil} />
      <PlayerStatusBar name={session.me.name} level={session.me.level} hp={hp} hpMax={hpMax} />
      <WorldMinimap game={engine?.game} size={160} worldRadius={480} />
      <ActionBar game={engine?.game} slots={actionSlots} />
      {toast && <FloatingToast text={toast} />}
    </>
  );
}

// ---- HP math (defensive) -----------------------------------------------

function currentHp(s: GameSession): number {
  const me = activeDigimon(s);
  if (!me) return maxHp(s);
  const hp = Number(me.currentHp);
  if (Number.isFinite(hp)) return Math.max(0, Math.round(hp));
  return maxHp(s);
}

function maxHp(s: GameSession): number {
  const me = activeDigimon(s);
  if (!me) return 100;
  // Backend formula approximation: base 50 + level*8 + ivHp/2 (placeholder
  // until /digimon/level-up gives us cached maxHp). Defaults guard NaN.
  const level = Number(me.level);
  const iv = Number(me.ivHp);
  const safeLevel = Number.isFinite(level) ? level : 1;
  const safeIv = Number.isFinite(iv) ? iv : 0;
  return Math.max(50, Math.round(50 + safeLevel * 8 + safeIv / 2));
}

function activeDigimon(s: GameSession): GameSession["digimons"][number] | undefined {
  if (!s.digimons || s.digimons.length === 0) return undefined;
  return s.digimons.find((d) => d.partySlot === 0) ?? s.digimons.find((d) => d.isInParty) ?? s.digimons[0];
}

function firstParty(s: GameSession): GameSession["digimons"][number] | undefined {
  return s.digimons.find((d) => d.isInParty) ?? s.digimons[0];
}

function firstChampionPlus(s: GameSession): GameSession["digimons"][number] | undefined {
  const tier = (st: string): number =>
    ({ baby: 0, in_training: 1, rookie: 2, champion: 3, ultimate: 4, mega: 5 })[st] ?? 0;
  return s.digimons.find((d) => tier(String(d.stage)) >= 3);
}

// ---- Tiny floating toast ------------------------------------------------

function FloatingToast({ text }: { text: string }) {
  return (
    <div
      style={{
        position: "fixed",
        top: 64,
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(15,18,24,0.94)",
        border: "1px solid rgba(168,85,247,0.6)",
        color: "#e6e8ee",
        padding: "6px 12px",
        borderRadius: 6,
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
        fontSize: 13,
        zIndex: 1300,
        pointerEvents: "none",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      }}
    >
      {text}
    </div>
  );
}
